import type { ISatSemanticTokens } from "@chatfood/design-system";
import { useDesignTokens } from "@chatfood/design-system";

export const useBranding = () => {
  const { semanticTokensToStyleString, semanticTokens } = useDesignTokens();

  const getTokensObj = (
    businessTokens: Record<string, string>,
  ): Partial<ISatSemanticTokens> => {
    const tokens: Partial<ISatSemanticTokens> = semanticTokens.value;

    Object.keys(businessTokens).forEach((key) => {
      if (!(`--${key}` in semanticTokens.value)) return;

      const typedKey = `--${key}` as keyof ISatSemanticTokens;
      const value = businessTokens[key];

      if (value) {
        tokens[typedKey] = value;
      }
    });

    return tokens;
  };

  const buildCustomFontRegister = (
    designTokens: Partial<ISatSemanticTokens>,
  ): string => {
    const fontFamilyHeadline =
      designTokens?.["--text-headline-font-family"]?.split(",")[0];
    const fontFamilyBase =
      designTokens?.["--text-base-font-family"]?.split(",")[0];
    const fonts = [""];

    if (fontFamilyHeadline) {
      fonts.push(`&family=${fontFamilyHeadline.replace(/\s+/g, "+")}`);
    }

    if (fontFamilyBase) {
      fonts.push(`&family=${fontFamilyBase.replace(/\s+/g, "+")}`);
    }

    return fonts.join("");
  };

  /**
   * Since Nuxt does not export the UseHeadInput type,
   * a manual definition of the return type is necessary.
   * This approach ensures type safety, as TypeScript
   * will flag any discrepancies between the manually defined
   * type and the structure expected by the `useHead` composable.
   */

  const getHeadTags = (
    businessTokens: Record<string, string>,
    businessLogo?: string,
    customCssSheet?: string,
  ): {
    meta?: Array<{ name: string; content: string }>;
    link?: Array<{ rel: string; href: string; crossorigin?: undefined }>;
    style?: Array<{ innerHTML: string; tagPriority: "critical" }>;
  } => {
    const designTokens = getTokensObj(businessTokens);
    const customFontRegister = buildCustomFontRegister(designTokens);

    const assetLinksToLoad = [
      { rel: "dns-prefetch", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.googleapis.com/" },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossorigin: undefined,
      },
      {
        href: `https://fonts.googleapis.com/css2?family=Inter${customFontRegister}&display=swap`,
        rel: "stylesheet",
      },
    ];

    if (customCssSheet) {
      assetLinksToLoad.push({
        href: customCssSheet,
        rel: "stylesheet",
      });
    }

    if (businessLogo) {
      assetLinksToLoad.push({
        rel: "icon",
        href: businessLogo,
      });
    }

    return {
      meta: [
        {
          name: "viewport",
          content: "width=device-width, initial-scale=1.0, maximum-scale=1.0",
        },
      ],
      link: assetLinksToLoad,
      style: [
        {
          innerHTML: semanticTokensToStyleString(designTokens, "body"),
          tagPriority: "critical",
        },
      ],
    };
  };

  return {
    getHeadTags,
  };
};
